import type { ProjectDetail } from '@core/core/types'
import { compareDates } from '@core/core/utils'
import createFuzzySearch from '@nozbe/microfuzz'
import { isDefined, unique } from 'common/helpers'

const CYRILLIC_TO_LATIN_TRANSLITERATION: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
}

const LATIN_TO_CYRILLIC_CASING: Record<string, string> = {
  '~': 'Ё',
  '!': '!',
  '@': '"',
  '#': '№',
  $: ';',
  '%': '%',
  '^': ':',
  '&': '?',
  '*': '*',
  '(': '(',
  ')': ')',
  Q: 'Й',
  W: 'Ц',
  E: 'У',
  R: 'К',
  T: 'Е',
  Y: 'Н',
  U: 'Г',
  I: 'Ш',
  O: 'Щ',
  P: 'З',
  '{': 'Х',
  '}': 'Ъ',
  '|': '/',
  A: 'Ф',
  S: 'Ы',
  D: 'В',
  F: 'А',
  G: 'П',
  H: 'Р',
  J: 'О',
  K: 'Л',
  L: 'Д',
  ':': 'Ж',
  '"': 'Э',
  Z: 'Я',
  X: 'Ч',
  C: 'С',
  V: 'М',
  B: 'И',
  N: 'Т',
  M: 'Ь',
  '<': 'Б',
  '>': 'Ю',
  '?': ',',
  '`': 'ё',
  q: 'й',
  w: 'ц',
  e: 'у',
  r: 'к',
  t: 'е',
  y: 'н',
  u: 'г',
  i: 'ш',
  o: 'щ',
  p: 'з',
  '[': 'х',
  ']': 'ъ',
  a: 'ф',
  s: 'ы',
  d: 'в',
  f: 'а',
  g: 'п',
  h: 'р',
  j: 'о',
  k: 'л',
  l: 'д',
  ';': 'ж',
  // eslint-disable-next-line quotes
  "'": 'э',
  z: 'я',
  x: 'ч',
  c: 'с',
  v: 'м',
  b: 'и',
  n: 'т',
  m: 'ь',
  ',': 'б',
  '.': 'ю',
  '/': '.',
}

class BiMap {
  private readonly backwardMap: Record<string, string>

  constructor(private readonly forwardMap: Record<string, string>) {
    this.backwardMap = Object.fromEntries(
      Object.entries(this.forwardMap).map(([key, value]) => [value, key]),
    )
  }

  getForward = (key: string): string | undefined => {
    return this.forwardMap[key]
  }

  getBackward = (key: string): string | undefined => {
    return this.backwardMap[key]
  }
}

const casingSwitcher = new BiMap(LATIN_TO_CYRILLIC_CASING)

function convertCasing(input: string, to: 'en' | 'ru') {
  const switcher = to === 'en' ? casingSwitcher.getBackward : casingSwitcher.getForward
  return input.split('').map(switcher).join('')
}

function transliterateCyrillicToLatin(input: string): string {
  return input
    .split('')
    .map(char => CYRILLIC_TO_LATIN_TRANSLITERATION[char.toLocaleLowerCase('ru')] || char)
    .join('')
}

export function prepareProjectList(
  projects: Partial<Record<string, ProjectDetail>>,
  user: string | undefined,
  query: string,
): ProjectDetail[] {
  // Базово мы сортируем сначала по дате, затем по пользователю (независимо, есть он или нет)
  // В худшем случае мы получим отсортированные по дате проекты, что уже лучше чем ничего
  // В лучшем случае у нас будут сгруппированные по пользователям и отсортированные по времени проекты
  const projectList = Object.values(projects)
    .filter(isDefined)
    .sort((one, other) => compareDates(one.changedAt, other.changedAt))
    .sort(project => {
      const isOneByCurrentUser = project.createdBy === user
      if (isOneByCurrentUser) return -1

      return 0
    })

  if (!query) return projectList

  // Чтобы поиск работал лучше, мы транслитирируем всю кириллицу в латиницу,
  // а также конвертируем раскладку клавиатуры в английскую и транслитирированную русскую
  const transliteratedQuery = transliterateCyrillicToLatin(query)
  const latinCasingQuery = convertCasing(query, 'en')
  const cyrillicCasingQuery = transliterateCyrillicToLatin(convertCasing(query, 'ru'))
  const queries = [transliteratedQuery, latinCasingQuery, cyrillicCasingQuery]
  // Это же касается и названий проектов
  const minimumProjectInfo = projectList.map(({ id, name }) => ({
    id,
    name: transliterateCyrillicToLatin(name),
  }))
  // Вообще, транслитерация не самый лучший способ сделать бесшовный поиск, потому что он не учитывает
  // letter similarity. Например, название скважины 17P может быть истолковано двояко: как латинская Пэ
  // и как русская Эр. Тогда алгоритм транслитерации не сможет сопоставить запрос 17Эр и 17Пэ, так
  // как они транслитируются в разные сущности: 17r и 17p соответственно

  const fuzzySearch = createFuzzySearch(minimumProjectInfo, { key: 'name', strategy: 'aggressive' })

  // Далее сортируем по тому, насколько название проекта близко к запросу
  const foundProjects = unique(
    queries
      .flatMap(fuzzySearch)
      .sort((one, other) => one.score - other.score)
      .map(searchResult => searchResult.item.id),
  )

  // Затем восстанавливаем изначальный проект по id
  return foundProjects.map(id => projects[id]).filter(isDefined)
}

export function constructUpdatedProject(
  newProject: ProjectDetail,
  oldProject?: ProjectDetail,
): ProjectDetail | undefined {
  if (oldProject) {
    return {
      ...oldProject,
      name: newProject.name,
      type: newProject.type,
      year: newProject.year,
      companyId: newProject.companyId,
      fieldId: newProject.fieldId,
    }
  }
  return undefined
}
